import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { coerceLocale, type SessionWithSets } from "@heraion/shared";
import { useTranslation } from "react-i18next";

import { formatShortDate } from "../Logger/formatDate";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { useSessionResultsFromDeps } from "../Progress/useSessionResults";
import type { ProgressDeps } from "../Progress/types";
import { SessionSetsList } from "../SessionSummary/SessionSetsList";
import { SessionSummaryView } from "../SessionSummary/SessionSummaryView";

// #25: one athlete session as the coach opens it. The summary on top when the
// athlete's SessionResult is in the deps' snapshot (the newest 200), then
// every set. Both read through the athlete-scoped deps: on web the cache the
// page already filled, on mobile the session screen's own. No energy or
// notes slot: health fields and notes stay with the athlete.

export type CoachSessionDetailProps = {
  deps: ProgressDeps;
  entry: SessionWithSets;
};

export function CoachSessionDetail({ deps, entry }: CoachSessionDetailProps) {
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const { theme, units } = deps;
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { results, catalog } = useSessionResultsFromDeps(deps);
  const resolveName = useExerciseNameResolver(catalog);
  const result = useMemo(
    () =>
      results?.find((r) => r.result.sessionId === entry.session.id)?.result ??
      null,
    [results, entry.session.id],
  );

  return (
    <View style={styles.wrap}>
      {result ? (
        <SessionSummaryView
          theme={theme}
          result={result}
          locale={locale}
          unit={units}
          resolveName={resolveName}
        />
      ) : (
        <View style={styles.head}>
          <Text style={styles.name} accessibilityRole="header">
            {entry.session.name ?? t("mobile.workouts.fallbackName")}
          </Text>
          <Text style={styles.date}>
            {formatShortDate(entry.session.startedAt, locale)}
          </Text>
        </View>
      )}
      <SessionSetsList
        theme={theme}
        sets={entry.sets}
        exerciseResults={result?.exerciseResults}
        resolveName={resolveName}
        unit={units}
        locale={locale}
      />
    </View>
  );
}

function makeStyles(theme: ProgressDeps["theme"]) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s4 },
    head: { gap: s.s1 },
    name: { color: p.ink, fontSize: ty.sizes.md, fontWeight: "700" },
    date: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
